import { Tariff, Course } from '../core/types';
import { createElement, appendChildren } from '../utils/dom-helpers';
import { createOrderUrl } from '../utils/formatter';
import { getYmGoal } from '../../../../pages/catalog/assets/js/utils/helpYmGoals';

// Расширяем интерфейс Window для TargetAds
declare global {
  interface Window {
    _targetadsTag?: (action: string, data: any) => void;
  }
}

// Функция для сбора данных TargetAds
function collectTargetAdsData(tariff: Tariff, course: Course): string {
  // Определяем цену (приоритет: новая цена за весь курс, новая цена за модуль, обычные цены)
  let price = 0;
  if (tariff.new_price_total && tariff.new_price_total > 0) {
    price = tariff.new_price_total;
  } else if (tariff.new_price_per_module && tariff.new_price_per_module > 0) {
    price = tariff.new_price_per_module;
  } else {
    price = tariff.price_total || tariff.price_per_module || 0;
  }
  
  // Определяем brand на основе типа курса из course.type
  let brand = ''; // по умолчанию пустая строка
  if (course.type && Array.isArray(course.type) && course.type.length > 0) {
    // Берем первый элемент из массива type как есть
    brand = course.type[0];
  }
  
  const targetAdsData = {
    id: course.id.toString(),
    title: course.short_title || course.title || '',
    category1: course.goal?.name || '',
    category2: course.discipline?.name || '',
    brand: brand,
    variant: tariff.title || '',
    price: price
  };
  
  return JSON.stringify(targetAdsData);
}

export function createTariffCard(tariff: Tariff, course: Course, index: number): HTMLElement {
  const card = createElement('div', 'tarif');
  if (tariff.is_popular) card.classList.add('popular');
  card.setAttribute('data-tariff-id', String(tariff.id));
  if (tariff.slug) card.setAttribute('data-tariff-slug', tariff.slug);

  if (tariff.is_popular) {
    const stickerWrapper = createElement('div', 'popular__sticker-wrapper');
    const sticker = createElement('p', 'popular__sticker');
    sticker.textContent = 'Самый популярный';
    stickerWrapper.appendChild(sticker);
    card.appendChild(stickerWrapper);
  }

  const title = createElement('p', 'tarif__title');
  title.textContent = tariff.title;
  card.appendChild(title);

  // Форматтер для цен
  const formatPrice = (value?: number) =>
    typeof value === 'number' ? new Intl.NumberFormat('ru-RU').format(value) : '';

  // Цена
  const priceBlock = createElement('div', 'tarif__price');

  // Проверяем отдельно новые цены для модулей и общей суммы
  const hasNewPricePerModule = tariff.new_price_per_module && tariff.new_price_per_module > 0;
  const hasNewPriceTotal = tariff.new_price_total && tariff.new_price_total > 0;

  // Проверяем условия для отображения только полной цены (теперь учитываем course.is_show_as_course)
  const isFullPriceOnly =
    course.is_show_as_course === true ||
    course.is_show_as_course === 1 ||
    course.is_show_as_course === '1' ||
    // Показываем только полную цену, если вообще нет помесячных цен
    (!tariff.price_per_module && !tariff.new_price_per_module) ||
    // Или если помесячная и полная цены равны (обычные или новые)
    (tariff.price_total && tariff.price_per_module && tariff.price_total === tariff.price_per_module) ||
    (tariff.new_price_total && tariff.new_price_per_module && tariff.new_price_total === tariff.new_price_per_module);

  if (isFullPriceOnly) {
    // Показываем только итоговую цену
    const totalPrice = hasNewPriceTotal ? 
      tariff.new_price_total : 
      tariff.price_total;
      
    const fullPriceWrapper = createElement('div', 'tarif__price__value full-price active');
    fullPriceWrapper.setAttribute('data-total-price', String(totalPrice));
    const fullPrice = createElement('p', 'tarif__price__value');
    fullPrice.textContent = `${formatPrice(totalPrice)} ₽`;
    fullPriceWrapper.appendChild(fullPrice);
    
    // Добавляем старую цену, если она есть и отличается от новой (только если есть новые цены)
    if (hasNewPriceTotal && tariff.price_total && tariff.new_price_total && tariff.new_price_total > 0 && tariff.price_total !== tariff.new_price_total) {
      const fullPriceInfo = createElement('div', 'tarif__price__full-price-info');
      const oldPriceSpan = createElement('span', '');
      oldPriceSpan.textContent = formatPrice(tariff.price_total);
      fullPriceInfo.appendChild(oldPriceSpan);
      fullPriceWrapper.appendChild(fullPriceInfo);
    }
    
    priceBlock.appendChild(fullPriceWrapper);
    priceBlock.setAttribute('data-only-full-price', 'true');
  } else {
    // Помесячная цена
    const monthlyPrice = createElement('p', `tarif__price__value monthly-price active`);
    
    // Для помесячной цены используем новую цену только если она есть, иначе обычную
    const monthlyPriceValue = hasNewPricePerModule ? tariff.new_price_per_module : tariff.price_per_module;
    const totalPriceValue = hasNewPriceTotal ? tariff.new_price_total : tariff.price_total;
    
    monthlyPrice.innerHTML = `${formatPrice(monthlyPriceValue)} ₽<span>/мес</span>`;
    monthlyPrice.setAttribute('data-total-price', String(totalPriceValue || ''));
    
    priceBlock.appendChild(monthlyPrice);

    // Полная цена (скрываем, если цены одинаковые)
    const fullPrice = createElement('div', `tarif__price__value full-price`);
    fullPrice.style.display = 'none';
    
    const totalPriceForFullPrice = hasNewPriceTotal ? tariff.new_price_total : tariff.price_total;
    const oldTotalPrice = hasNewPriceTotal ? tariff.price_total : null;
    
    if (totalPriceForFullPrice) {
      const fullPriceValue = createElement('p', 'tarif__price__value');
      fullPriceValue.textContent = `${formatPrice(totalPriceForFullPrice)} ₽`;
      fullPrice.appendChild(fullPriceValue);
      
      // Информация о полной цене (старая цена, только если есть новая цена за весь курс)
      if (hasNewPriceTotal && oldTotalPrice && oldTotalPrice !== totalPriceForFullPrice) {
        const fullPriceInfo = createElement('div', 'tarif__price__full-price-info');
        const span = createElement('span', '');
        span.textContent = formatPrice(oldTotalPrice);
        fullPriceInfo.appendChild(span);
        fullPrice.appendChild(fullPriceInfo);
      }
      priceBlock.appendChild(fullPrice);
    }

    // Длительность (показываем старую цену за модуль только если есть акция на модули)
    if (hasNewPricePerModule && tariff.price_per_module && tariff.new_price_per_module && tariff.price_per_module !== tariff.new_price_per_module) {
      const pricePerModuleForDuration = tariff.price_per_module; // Старая цена за модуль
      const totalPriceForDuration = tariff.price_total; // Старая общая цена
      
      const duration = createElement('div', 'tarif__price__duration');
      const p = createElement('p', '');
      p.setAttribute('data-total-price', totalPriceForDuration ? String(totalPriceForDuration) : '');
      p.textContent = formatPrice(pricePerModuleForDuration) + ' ₽';
      duration.appendChild(p);
      priceBlock.appendChild(duration);
    }
  }

  card.appendChild(priceBlock);

  // Описание
  const desc = createElement('div', 'tarif__description');
  const pDesc = createElement('p', '');
  pDesc.textContent = tariff.short_description || '';
  desc.appendChild(pDesc);
  card.appendChild(desc);

  // Особенности (features)
  if (tariff.description) {
    const features = createElement('ul', 'tarif__features');
    const featureLines = tariff.description.split(/\r?\n/).map(f => f.trim()).filter(Boolean);
    featureLines.forEach(feature => {
      let text = feature;
      let tooltip = '';
      const tooltipMatch = feature.match(/i\((.*?)\)i/u);
      if (tooltipMatch) {
        text = feature.replace(tooltipMatch[0], '').trim();
        tooltip = tooltipMatch[1];
      }
      const li = createElement('li', 'tarif__feature');
      const tickIcon = createElement('img', '');
      tickIcon.setAttribute('src', `${(window as any).themeUrl || '/wp-content/themes/100points'}/pages/catalog/assets/icons/tick.svg`);
      tickIcon.setAttribute('alt', 'Галочка');
      li.appendChild(tickIcon);
      li.appendChild(document.createTextNode(text));
      if (tooltip) {
        const tooltipDiv = createElement('div', 'tooltip');
        tooltipDiv.innerHTML = `<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 0.5C3.6 0.5 0 4.1 0 8.5C0 12.9 3.6 16.5 8 16.5C12.4 16.5 16 12.9 16 8.5C16 4.1 12.4 0.5 8 0.5ZM8 14.5C4.7 14.5 2 11.8 2 8.5C2 5.2 4.7 2.5 8 2.5C11.3 2.5 14 5.2 14 8.5C14 11.8 11.3 14.5 8 14.5Z" fill="#A0A0A0" /><path d="M9 7.50049H7V12.5005H9V7.50049ZM9 4.50049H7V6.50049H9V4.50049Z" fill="#A0A0A0" /></svg>`;
        const tooltipText = createElement('div', 'tooltip__text');
        const tooltipP = createElement('p', '');
        tooltipP.textContent = tooltip;
        tooltipText.appendChild(tooltipP);
        tooltipDiv.appendChild(tooltipText);
        li.appendChild(tooltipDiv);
      }
      features.appendChild(li);
    });
    card.appendChild(features);
  }

  // Кнопка выбора
  const orderUrl = `/order/?course_id=${course.id}&tariff_id=${tariff.id}&type=${course.is_show_as_course ? 'course' : 'module'}`;
  const link = createElement('a', 'tarif__link btn-clrd orange');
  link.setAttribute('href', orderUrl);
  link.setAttribute('data-action', 'order');
  link.setAttribute('data-tariff-id', String(tariff.id));
  link.setAttribute('data-course', String(course.id));
  link.setAttribute('data-type', course.is_show_as_course ? 'course' : 'module');
  
  // Добавляем цель для метрики
  // TODO: Нужно определить, как различать родительские и студенческие модалки
  const ymGoal = getYmGoal('select'); // Функция автоматически определит контекст (каталог/родители/студенты)
  if (ymGoal) link.setAttribute('data-ym-goal', ymGoal);
  
  // Добавляем параметры для метрики
  link.setAttribute('data-ym-params', `{"course_id":"${course.id}","tariff_id":"${tariff.id}"}`);
  
  // Добавляем данные TargetAds
  const targetAdsData = collectTargetAdsData(tariff, course);
  link.setAttribute('data-tads-params', targetAdsData);
  
  // Добавляем обработчик для отправки данных в TargetAds
  link.addEventListener('click', function(event) {
    try {
      const tadsData = JSON.parse(targetAdsData);
      
      // Проверяем наличие TargetAds на странице
      if (typeof window._targetadsTag === 'function') {
        window._targetadsTag('add_to_cart', tadsData);
        console.log('TargetAds: отправлены данные add_to_cart', tadsData);
      } else {
        console.warn('TargetAds: _targetadsTag не найден на странице');
      }
    } catch (error) {
      console.error('TargetAds: ошибка при отправке данных', error);
    }
  });
  
  link.textContent = 'Выбрать';
  card.appendChild(link);

  return card;
} 